// src/utils/listenerResults.js
//
// The live results of a store's Firestore listeners on one collection, merged by document id: the shape leadStore and
// commandBoardStore use, as a helper for dataStore and allCustomersStore
// (docs/bugs/customer_stores_drop_snapshot_batches_BUG.md; tickets pxewdlx23g2vyHaoPyq4, osjQpGqRMtq37YkqaQd3).
//
// Each listener keeps its OWN full result, rebuilt from every snapshot it delivers, and the store shows the union. So
// no snapshot can be lost between two others (the old processors passed each snapshot's docChanges() to one lodash
// debounce, which runs with only the first and the last call's batch of a burst), and a row one listener drops while
// another still holds it stays.
//
// A row its snapshot did not change (not in that snapshot's docChanges()) keeps its object from the listener's previous
// result: a snapshot converts only the documents it changed, and unchanged rows stay identical downstream.

/**
 * One listener's full result after a snapshot, as `{ [id]: { id, ...data } }`.
 * @param {import('firebase/firestore').QuerySnapshot} snapshot
 * @param {Record<string, object>} previousRows  the same listener's previous result
 * @returns {Record<string, object>}
 */
export function snapshotRows(snapshot, previousRows = {}) {
  const changed = new Set()
  for (const change of snapshot.docChanges()) {
    if (change.type !== 'removed') changed.add(change.doc.id)
  }
  const rows = {}
  snapshot.forEach(d => {
    rows[d.id] =
      !changed.has(d.id) && previousRows[d.id] ? previousRows[d.id] : { id: d.id, ...d.data() }
  })
  return rows
}

/**
 * A store's listener parts, each under a key naming its query (e.g. 'active', `owned:${uid}`).
 *
 * A resubscribe (a tab resume, a forced refetch) opens its listeners under the same keys and calls retain(keys) first:
 * their last results stay, so the list does not blink while the first snapshots arrive, and each part is replaced by
 * its listener's first snapshot (a row that left while the tab slept goes then). A part no new listener reopens (the
 * user's visibility tier changed) is dropped.
 */
export function createListenerResults() {
  const parts = new Map()
  return {
    /** Replace a listener's part with its snapshot's full result; returns the new rows and the part they replace. */
    apply(key, snapshot) {
      const previous = parts.get(key) || {}
      const rows = snapshotRows(snapshot, previous)
      // Re-inserted last: where listeners overlap, the one that answered most recently (the newest rows) wins.
      parts.delete(key)
      parts.set(key, rows)
      return { rows, previous }
    },
    /** Keep only these listeners' parts. */
    retain(keys) {
      const keep = new Set(keys)
      for (const key of [...parts.keys()]) {
        if (!keep.has(key)) parts.delete(key)
      }
    },
    /** The union of every part, as a new `{ [id]: row }` object. */
    merged() {
      return Object.assign({}, ...parts.values())
    },
    clear() {
      parts.clear()
    },
  }
}
